import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import {
  Activity, ShieldCheck, ShieldAlert, AlertTriangle, Zap,
  Server, Ban, Bot, RefreshCw, TrendingUp, Gauge
} from 'lucide-react';
import { toast } from 'sonner';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HEALTH_STYLES = {
  green: { bg: 'bg-emerald-50 border-emerald-200', dot: 'bg-emerald-500', text: 'text-emerald-800', label: 'All Systems Operational' },
  yellow: { bg: 'bg-amber-50 border-amber-200', dot: 'bg-amber-500', text: 'text-amber-800', label: 'Degraded - Monitoring' },
  red: { bg: 'bg-red-50 border-red-200', dot: 'bg-red-500', text: 'text-red-800', label: 'Protection Active' },
};

const LEVEL_BADGES = {
  normal: 'bg-emerald-100 text-emerald-700',
  warning: 'bg-amber-100 text-amber-700',
  protection: 'bg-orange-100 text-orange-700',
  emergency: 'bg-red-100 text-red-700',
  critical: 'bg-red-600 text-white',
};

const StatCard = ({ icon: Icon, label, value, sub, testId }) => (
  <div data-testid={testId} className="bg-white rounded-xl border border-neutral-200 p-5">
    <div className="flex items-center gap-2 text-neutral-500 text-sm mb-2">
      <Icon className="w-4 h-4" /> {label}
    </div>
    <div className="text-2xl font-bold text-neutral-900">{value}</div>
    {sub && <div className="text-xs text-neutral-400 mt-1">{sub}</div>}
  </div>
);

export default function AdminOps() {
  const [data, setData] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      const [dash, al, cfg] = await Promise.all([
        axios.get(`${API}/admin/ops/dashboard`),
        axios.get(`${API}/admin/ops/alerts`),
        axios.get(`${API}/admin/ops/config`),
      ]);
      setData(dash.data);
      setAlerts(al.data.alerts);
      setConfig(cfg.data);
    } catch (e) {
      toast.error('Failed to load ops dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
    const interval = setInterval(fetchAll, 30000);
    return () => clearInterval(interval);
  }, [fetchAll]);

  const saveConfig = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/ops/config`, {
        daily_request_budget: Number(config.daily_request_budget),
        max_entries_per_user_per_day: Number(config.max_entries_per_user_per_day),
        breaker_enabled: config.breaker_enabled,
      });
      toast.success('Protection settings saved');
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const resetBreaker = async (mode) => {
    try {
      await axios.post(`${API}/admin/ops/circuit-breaker/reset`, { mode });
      toast.success(mode === 'force_normal' ? 'Circuit breaker forced to normal' : 'Circuit breaker set to automatic');
      fetchAll();
    } catch (e) {
      toast.error('Failed to update circuit breaker');
    }
  };

  if (loading || !data) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-10 h-10 border-4 border-[#FF3B30]/20 border-t-[#FF3B30] rounded-full animate-spin" />
      </div>
    );
  }

  const h = HEALTH_STYLES[data.health] || HEALTH_STYLES.green;
  const pct = Math.min(100, data.circuit.pct_of_budget);

  return (
    <div data-testid="admin-ops-page" className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900 flex items-center gap-2">
            <Activity className="w-6 h-6 text-[#FF3B30]" /> Ops & Usage Protection
          </h1>
          <p className="text-sm text-neutral-500 mt-1">Live traffic monitoring, rate limiting and automatic circuit breaker</p>
        </div>
        <Button data-testid="ops-refresh-btn" variant="outline" size="sm" onClick={fetchAll}>
          <RefreshCw className="w-4 h-4 mr-2" /> Refresh
        </Button>
      </div>

      {/* Health banner */}
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
        data-testid="ops-health-banner"
        className={`rounded-xl border p-4 flex items-center justify-between flex-wrap gap-3 ${h.bg}`}>
        <div className="flex items-center gap-3">
          <span className={`w-3 h-3 rounded-full ${h.dot} animate-pulse`} />
          <span className={`font-semibold ${h.text}`}>{h.label}</span>
          <span data-testid="ops-circuit-level" className={`text-xs font-bold px-2.5 py-1 rounded-full uppercase ${LEVEL_BADGES[data.circuit.level]}`}>
            {data.circuit.level}
          </span>
          {data.circuit.override === 'force_normal' && (
            <span className="text-xs px-2 py-1 rounded-full bg-neutral-200 text-neutral-700">Manual override active</span>
          )}
        </div>
        <div className="flex gap-2">
          <Button data-testid="ops-breaker-auto-btn" size="sm" variant="outline" onClick={() => resetBreaker('auto')}>
            <ShieldCheck className="w-4 h-4 mr-1" /> Auto Mode
          </Button>
          <Button data-testid="ops-breaker-force-btn" size="sm" variant="outline" onClick={() => resetBreaker('force_normal')}>
            <ShieldAlert className="w-4 h-4 mr-1" /> Force Normal
          </Button>
        </div>
      </motion.div>

      {/* Budget usage bar */}
      <div className="bg-white rounded-xl border border-neutral-200 p-5">
        <div className="flex justify-between text-sm mb-2">
          <span className="font-medium text-neutral-700 flex items-center gap-2"><Gauge className="w-4 h-4" /> Daily Request Budget</span>
          <span data-testid="ops-budget-pct" className="font-semibold text-neutral-900">
            {data.today.requests.toLocaleString()} / {data.circuit.daily_budget.toLocaleString()} ({data.circuit.pct_of_budget}%)
          </span>
        </div>
        <div className="h-3 bg-neutral-100 rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all ${pct >= 95 ? 'bg-red-500' : pct >= 85 ? 'bg-orange-500' : pct >= 70 ? 'bg-amber-400' : 'bg-emerald-500'}`}
            style={{ width: `${Math.max(2, pct)}%` }}
          />
        </div>
        <div className="flex justify-between text-[10px] text-neutral-400 mt-1">
          <span>0%</span><span>70% warn</span><span>85% protect</span><span>95% emergency</span><span>100%</span>
        </div>
      </div>

      {/* Today stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Server} label="Requests Today" value={data.today.requests.toLocaleString()} sub={`${data.today.writes} writes`} testId="ops-stat-requests" />
        <StatCard icon={AlertTriangle} label="Errors Today" value={data.today.errors} sub={`${data.today.error_rate}% error rate`} testId="ops-stat-errors" />
        <StatCard icon={Ban} label="Blocked (Rate Limit)" value={data.today.blocked_rate_limited} sub={`${data.today.blocked_circuit} circuit-blocked`} testId="ops-stat-blocked" />
        <StatCard icon={Bot} label="AI / Heavy Calls" value={data.today.heavy_requests} sub={`${data.today.auth_requests} auth requests`} testId="ops-stat-heavy" />
      </div>

      {/* Monthly + projection */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <StatCard icon={TrendingUp} label="Requests This Month" value={data.month.requests.toLocaleString()} sub={`${data.month.errors} errors · ${data.month.blocked} blocked`} testId="ops-stat-month" />
        <StatCard icon={Zap} label="Projected End of Month" value={data.month.projected_requests_eom.toLocaleString()} sub={`${data.month.projected_pct_of_budget}% of monthly budget`} testId="ops-stat-projection" />
        <div data-testid="ops-forecast-card" className={`rounded-xl border p-5 ${data.month.projected_pct_of_budget > 100 ? 'bg-red-50 border-red-200' : 'bg-white border-neutral-200'}`}>
          <div className="flex items-center gap-2 text-neutral-500 text-sm mb-2"><Gauge className="w-4 h-4" /> Budget Forecast</div>
          <div className={`text-lg font-bold ${data.month.projected_pct_of_budget > 100 ? 'text-red-700' : 'text-emerald-700'}`}>
            {data.month.projected_pct_of_budget > 100 ? 'Over budget trajectory' : 'On track'}
          </div>
          <div className="text-xs text-neutral-400 mt-1">Monthly budget: {data.month.monthly_budget.toLocaleString()} requests</div>
        </div>
      </div>

      {/* 14-day history */}
      <div className="bg-white rounded-xl border border-neutral-200 p-5">
        <h2 className="font-semibold text-neutral-900 mb-4">Last 14 Days</h2>
        <div className="h-56">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data.history}>
              <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(d) => d.slice(5)} />
              <YAxis tick={{ fontSize: 10 }} width={40} />
              <Tooltip />
              <Bar dataKey="requests" fill="#FF3B30" radius={[4, 4, 0, 0]} name="Requests" />
              <Bar dataKey="blocked" fill="#f59e0b" radius={[4, 4, 0, 0]} name="Blocked" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Config + Alerts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white rounded-xl border border-neutral-200 p-5 space-y-4">
          <h2 className="font-semibold text-neutral-900">Protection Settings</h2>
          {config && (
            <>
              <div className="flex items-center justify-between">
                <Label htmlFor="breaker-toggle" className="text-sm">Automatic circuit breaker</Label>
                <Switch id="breaker-toggle" data-testid="ops-breaker-toggle" checked={config.breaker_enabled}
                  onCheckedChange={(v) => setConfig({ ...config, breaker_enabled: v })} />
              </div>
              <div>
                <Label className="text-sm">Daily request budget</Label>
                <Input data-testid="ops-budget-input" type="number" className="mt-1" value={config.daily_request_budget}
                  onChange={(e) => setConfig({ ...config, daily_request_budget: e.target.value })} />
              </div>
              <div>
                <Label className="text-sm">Max giveaway entries per user per day</Label>
                <Input data-testid="ops-entry-limit-input" type="number" className="mt-1" value={config.max_entries_per_user_per_day}
                  onChange={(e) => setConfig({ ...config, max_entries_per_user_per_day: e.target.value })} />
              </div>
              <Button data-testid="ops-save-config-btn" onClick={saveConfig} disabled={saving} className="bg-[#FF3B30] hover:bg-[#e0342a] text-white">
                {saving ? 'Saving...' : 'Save Settings'}
              </Button>
            </>
          )}
        </div>

        <div className="bg-white rounded-xl border border-neutral-200 p-5">
          <h2 className="font-semibold text-neutral-900 mb-3">Alert History</h2>
          <div data-testid="ops-alerts-list" className="space-y-2 max-h-80 overflow-y-auto">
            {alerts.length === 0 && <p className="text-sm text-neutral-400">No circuit breaker events yet. All quiet.</p>}
            {alerts.map((a) => (
              <div key={a.alert_id} className="text-sm border border-neutral-100 rounded-lg p-3">
                <div className="flex items-center justify-between">
                  <span className={`text-xs font-bold px-2 py-0.5 rounded-full uppercase ${LEVEL_BADGES[a.to_level] || ''}`}>
                    {a.from_level} → {a.to_level}
                  </span>
                  <span className="text-[11px] text-neutral-400">{new Date(a.timestamp).toLocaleString()}</span>
                </div>
                <p className="text-neutral-700 mt-1">{a.trigger}</p>
                <p className="text-xs text-neutral-400">{a.action_taken}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
